import { forwardRef } from "react";
import type { HTMLAttributes, ReactNode } from "react";
import { cn } from "@/lib/utils";

/**
 * Card canonica L1 del design system (§1/§6): filo d'oro superiore,
 * bordo `white/[0.06]`, hover lift e slot header/body/footer.
 */

export type SurfaceCardVariant = "flat" | "raised" | "focus";
export type SurfaceCardPadding = "none" | "sm" | "md" | "lg";

export interface SurfaceCardProps extends Omit<HTMLAttributes<HTMLDivElement>, "title"> {
  /** L1 piatta (default), L2 in rilievo, L3 in fuoco (§1). */
  variant?: SurfaceCardVariant;
  /**
   * Colore-dato del filo e dell'alone (brand cliente, stato pipeline…).
   * È un DATO, quindi può arrivare come hex/hsl inline. Default: oro brand.
   */
  accentColor?: string;
  /** Contenuto della fascia superiore, separato da hairline. */
  header?: ReactNode;
  /** Contenuto della fascia inferiore, separato da hairline. */
  footer?: ReactNode;
  /** Strato decorativo a tutta card (es. sparkline che sborda). */
  overlay?: ReactNode;
  /** Sollevamento e accensione del bordo in hover (default true). */
  interactive?: boolean;
  /** Padding del corpo: md = 20px (L1), lg = 24px (L2). */
  padding?: SurfaceCardPadding;
  /** Classi aggiuntive sul solo corpo. */
  bodyClassName?: string;
  children?: ReactNode;
}

const GOLD = "hsl(var(--primary))";

const variantClasses: Record<SurfaceCardVariant, string> = {
  flat: "border-white/[0.06] bg-card",
  raised: "border-primary/20 bg-card-2 shadow-renaissance",
  focus: "border-primary/35 bg-primary/[0.06] shadow-glow-primary",
};

const hoverClasses: Record<SurfaceCardVariant, string> = {
  flat: "hover:-translate-y-0.5 hover:border-primary/25",
  raised: "hover:-translate-y-0.5 hover:border-primary/35",
  focus: "hover:-translate-y-0.5 hover:border-primary/50",
};

const paddingClasses: Record<SurfaceCardPadding, string> = {
  none: "",
  sm: "p-3",
  md: "p-5",
  lg: "p-6",
};

const bandPaddingClasses: Record<SurfaceCardPadding, string> = {
  none: "",
  sm: "px-3 py-2",
  md: "px-5 py-3",
  lg: "px-6 py-4",
};

export const SurfaceCard = forwardRef<HTMLDivElement, SurfaceCardProps>(
  (
    {
      variant = "flat",
      accentColor,
      header,
      footer,
      overlay,
      interactive = true,
      padding = "md",
      bodyClassName,
      className,
      children,
      ...props
    },
    ref,
  ) => {
    const accent = accentColor ?? GOLD;
    const band = bandPaddingClasses[padding === "none" ? "md" : padding];

    return (
      <div
        ref={ref}
        className={cn(
          "group relative min-w-0 overflow-hidden rounded-[var(--radius)] border",
          "transition-[transform,border-color,box-shadow] duration-fast ease-palazzo will-change-transform",
          variantClasses[variant],
          interactive && hoverClasses[variant],
          className,
        )}
        {...props}
      >
        {/* Filo d'oro: 0.4 a riposo, 1 in hover. Firma della casa (§1). */}
        <span
          aria-hidden
          className="pointer-events-none absolute inset-x-0 top-0 z-[1] h-px opacity-40 transition-opacity duration-base ease-palazzo group-hover:opacity-100"
          style={{ background: `linear-gradient(90deg, transparent, ${accent}, transparent)` }}
        />
        {/* Alone smorzato del colore-dato, affiora solo al passaggio. */}
        <span
          aria-hidden
          className="pointer-events-none absolute -right-10 -top-12 h-32 w-32 rounded-full opacity-0 blur-3xl transition-opacity duration-slow group-hover:opacity-[0.12]"
          style={{ background: accent }}
        />

        {overlay && <div className="pointer-events-none absolute inset-0">{overlay}</div>}

        {header && (
          <div className={cn("relative border-b border-white/[0.06]", band)}>{header}</div>
        )}

        <div className={cn("relative", paddingClasses[padding], bodyClassName)}>{children}</div>

        {footer && (
          <div className={cn("relative border-t border-white/[0.06]", band)}>{footer}</div>
        )}
      </div>
    );
  },
);
SurfaceCard.displayName = "SurfaceCard";
